import { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type { IBundleProduct, IDexfSettingsLocalisation, IProduct } from '@inter-ikea-kompis/types';

import StringUtility from '../string/StringUtility.js';
import type IProductDescriptionLocalizedInformation from './IProductDescriptionLocalizedInformation.js';
import type IProductDescriptionTranslations from './IProductDescriptionTranslations.js';
import ProductDescriptionProductMeasureEnum from './ProductDescriptionProductMeasureEnum.js';
import ProductUtility from './ProductUtility.js';

type IProductDescriptionDexfSettings = {
	localisation: Pick<IDexfSettingsLocalisation, 'useMetricMeasures'>;
};

type GetDescriptionType = {
	/** @deprecated Use settings and translations instead. */
	localizedInformation?: IProductDescriptionLocalizedInformation;
	settings?: IProductDescriptionDexfSettings;
	translations?: IProductDescriptionTranslations;
	product: IProduct | IBundleProduct;
	include?: {
		productMeasures?: ProductDescriptionProductMeasureEnum;
		packageMeasures?: boolean;
		designText?: boolean;
		weight?: boolean;
		measurementSystem?: MeasurementSystemEnum | null;
	};
	exclude?: {
		typeName?: boolean;
	};
};

type GetProductMeasureType = {
	/** @deprecated Use settings instead. */
	localizedInformation?: IProductDescriptionLocalizedInformation;
	settings?: IProductDescriptionDexfSettings;
	product: IProduct;
	productMeasures: ProductDescriptionProductMeasureEnum;
	measurementSystem?: MeasurementSystemEnum | null;
};

type GetProductWeightType = {
	settings: IProductDescriptionDexfSettings;
	translations: IProductDescriptionTranslations;
	product: IProduct;
	include?: {
		label?: boolean;
		measurementSystem?: MeasurementSystemEnum | null;
	};
};

type GetPackageMeasuresType = {
	settings: IProductDescriptionDexfSettings;
	translations: IProductDescriptionTranslations;
	product: IProduct;
	measurementSystem?: MeasurementSystemEnum | null;
};

/**
 * Product description utility.
 */
export default class ProductDescriptionUtility {
	/**
	 * Returns product description.
	 *
	 * @param options Options.
	 * @param options.product Product.
	 * @param options.localizedInformation Deprecated - Use translations and settings instead.
	 * @param options.include Include in description.
	 * @param options.include.designText Design text.
	 * @param options.include.packageMeasures Package measures.
	 * @param options.include.productMeasures Product measures.
	 * @param options.include.weight Weight.
	 * @param options.include.measurementSystem ...
	 * @param options.exclude.typeName TypeName
	 * @param options.settings Settings from DEXF.
	 * @param options.translations Translations.
	 * @returns Product description.
	 */
	public static getDescription(options: GetDescriptionType): string {
		const { settings, localizedInformation, product, include, exclude, translations } = options;
		const actualTranslations = translations ?? localizedInformation?.translations;
		const isBundleProduct = ProductUtility.isBundleProduct(product);

		const actualSettings =
			settings ??
			(localizedInformation
				? {
						localisation: {
							useMetricMeasures: localizedInformation.localization.useMetricMeasures
						}
					}
				: null);

		if (!actualSettings || !actualTranslations) {
			throw Error(
				'Either translations and settings parameters or localizedInformation parameter must be set.'
			);
		}

		const description: string[] = [];

		if (product.content?.typeName && !exclude?.typeName) {
			description.push(product.content?.typeName);
		}

		// Design text
		if (include?.designText) {
			const designText = this.getProductDesignText(product);

			if (designText) {
				description.push(designText);
			}
		}

		// Product measures
		if (!isBundleProduct && include?.productMeasures) {
			const productMeasures = this.getProductMeasures({
				settings: actualSettings,
				productMeasures: include.productMeasures,
				product: product,
				measurementSystem: include.measurementSystem
			});

			if (productMeasures && typeof productMeasures === 'string') {
				description.push(productMeasures);
			}
		}

		// Package measures
		if (!isBundleProduct && include?.packageMeasures) {
			const packageMeasures = this.getPackageMeasures({
				settings: actualSettings,
				product: product,
				translations: actualTranslations,
				measurementSystem: include.measurementSystem
			});

			if (packageMeasures) {
				description.push(packageMeasures);
			}
		}

		// Weight
		if (!isBundleProduct && include?.weight) {
			const weight = this.getProductWeight({
				settings: actualSettings,
				product: product,
				translations: actualTranslations,
				include: {
					label: true,
					measurementSystem: include.measurementSystem
				}
			});

			if (weight) {
				description.push(weight);
			}
		}

		// Only capitalize if there actually is any description.
		const capitalizedDescription = description.length
			? StringUtility.capitalizeFirstLetter(description.filter((part) => part != '').join(', '))
			: '';

		return capitalizedDescription;
	}

	/**
	 * Returns product measures.
	 *
	 * @param options Options.
	 * @param options.localizedInformation Deprecated - Use settings instead.
	 * @param options.productMeasures Product measure.
	 * @param options.product Product.
	 * @param options.settings Settings from DEXF.
	 * @param options.measurementSystem ...
	 * @returns Returns a text with all product measurements.
	 */
	public static getProductMeasures(
		options: GetProductMeasureType
	): string | null | { typeName: string; measure: string }[] {
		const { settings, localizedInformation, product, productMeasures, measurementSystem } = options;

		const useMetricMeasures =
			settings?.localisation.useMetricMeasures ??
			localizedInformation?.localization.useMetricMeasures;

		if (useMetricMeasures === undefined) {
			throw Error('Either options.settings or options.localizedInformation must be set.');
		}

		switch (productMeasures) {
			case ProductDescriptionProductMeasureEnum.short: {
				if (!product.content?.measureReference) {
					return null;
				}

				const { textMetric, textImperial } = product.content.measureReference;

				const shortMeasure = (useMetric: boolean): string => {
					return useMetric ? textMetric : textImperial;
				};

				if (measurementSystem) {
					return shortMeasure(measurementSystem === MeasurementSystemEnum.metric);
				}

				return shortMeasure(useMetricMeasures);
			}

			case ProductDescriptionProductMeasureEnum.long: {
				if (!product.content?.measure) {
					return null;
				}

				const measure = product.content.measure;

				const longMeasure = (useMetric: boolean): string => {
					return measure
						.map(
							({ typeName, textImperial, textMetric }) =>
								`${typeName}: ${useMetric ? textMetric : textImperial}`
						)
						.join(', ');
				};

				if (measurementSystem) {
					return longMeasure(measurementSystem === MeasurementSystemEnum.metric);
				}

				return longMeasure(useMetricMeasures);
			}

			case ProductDescriptionProductMeasureEnum.array: {
				if (!product.content?.measure) {
					return null;
				}

				const measure = product.content.measure;

				const arrayMeasure = (useMetric: boolean): { typeName: string; measure: string }[] => {
					return measure.map(({ typeName, textMetric, textImperial }) => {
						return {
							typeName: typeName,
							measure: useMetric ? textMetric : textImperial
						};
					});
				};

				if (measurementSystem) {
					return arrayMeasure(measurementSystem === MeasurementSystemEnum.metric);
				}

				return arrayMeasure(useMetricMeasures);
			}
		}

		return null;
	}

	/**
	 * Returns weight.
	 *
	 * @param options Options.
	 * @param options.product Product.
	 * @param options.settings Settings from DEXF.
	 * @param options.translations Translations.
	 * @param options.include Extra parameters to include values.
	 * @param options.include.label Include label.
	 * @returns Template.
	 */
	public static getProductWeight(options: GetProductWeightType): string | null {
		const { settings, include, product, translations } = options;
		const packageMeasure = product.content?.packageMeasure;

		if (!packageMeasure) {
			return null;
		}

		const useMetricMeasures = settings.localisation.useMetricMeasures;
		const measurementSystem = include?.measurementSystem ?? null;
		const withLabel = include?.label ?? false;

		const packageWeights = (condition: boolean, label = false): string => {
			const packageLabel = `${label ? `${translations.pickingListSortPackageWeight}: ` : ''}`;
			const packageWeights = `${packageMeasure.package
				.map((p) => (condition ? p.weightGrossMetric : p.weightGrossImperial))
				.join(', ')}`;

			return `${packageLabel}${packageWeights}`;
		};

		if (measurementSystem) {
			return packageWeights(measurementSystem === MeasurementSystemEnum.metric, withLabel);
		}

		return packageWeights(useMetricMeasures, withLabel);
	}

	/**
	 * Returns package measures.
	 *
	 * @param options Options.
	 * @param options.product Product.
	 * @param options.settings Settings from DEXF.
	 * @param options.translations Translations.
	 * @param options.measurementSystem ...
	 * @returns Returns a text with all package measurements.
	 */
	private static getPackageMeasures(options: GetPackageMeasuresType): string | null {
		const { settings, product, translations, measurementSystem } = options;

		if (!product.content?.packageMeasure) {
			return null;
		}

		const packages = product.content.packageMeasure.package;
		const useMetricMeasures = settings.localisation.useMetricMeasures;

		const packageMeasures = (useMetric: boolean): string => {
			return `${translations.pickingListSortPackageSize}: ${packages
				.map((p) => {
					if (!useMetric) {
						return `${p.lengthImperial} x ${p.widthImperial} x ${p.heightImperial}`;
					}
					return `${p.lengthMetric} x ${p.widthMetric} x ${p.heightMetric}`;
				})
				.join(', ')}`;
		};

		if (measurementSystem) {
			return packageMeasures(measurementSystem === MeasurementSystemEnum.metric);
		}

		return packageMeasures(useMetricMeasures);
	}

	/**
	 * Returns design text.
	 *
	 * @param product Product.
	 * @returns Design text.
	 */
	private static getProductDesignText(product: IProduct | IBundleProduct): string {
		if (!product.content?.validDesignText) {
			return '';
		}

		return product.content.validDesignText;
	}
}
